/**
 * Banner compartilhado "Módulo gratuito limitado" exibido em todas as
 * telas que respeitam o limite de 10 cobranças/mês do plano sem o
 * add-on Módulo Financeiro.
 *
 * Usado em:
 *  - /financial/charges (FinancialCharges.tsx)
 *  - /financial/subscriptions (FinancialSubscriptions.tsx)
 *  - /financial/installments (FinancialInstallments.tsx)
 *  - /tenant/settings → aba Financeiro (FinancialSettingsCard.tsx)
 *
 * Quando `hasFinancialModule` é true, o banner não renderiza nada.
 *
 * RELEASE: 2026-06-16 v1.0.0 - Versão inicial. Extrai JSX antes duplicado
 *          em 3 páginas do módulo Financeiro para um único componente.
 */
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Crown, Package } from 'lucide-react';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { useFinancialChargeCount } from '@/hooks/useFinancialChargeCount';

export type FreeTierEntity = 'cobranças' | 'recorrências' | 'parcelas';

export interface FreeTierLimitBannerProps {
  /**
   * Label da entidade exibida no banner. Por padrão "cobranças" (igual ao
   * texto histórico da página de Cobranças).
   */
  entityLabel?: FreeTierEntity;
  /**
   * Container externo. Quando omitido, o componente envolve em
   * `<div className="shrink-0 px-4 py-3 border-b">` (padrão das páginas
   * do módulo). Passe `false` para renderizar somente o Card (útil
   * dentro de outros cards, como na aba de Configurações).
   */
  withContainer?: boolean;
}

/**
 * Renderiza o banner amarelo/laranja que comunica o limite gratuito
 * do módulo Financeiro. Self-contained: já consulta `useResourceLimits`
 * e `useFinancialChargeCount` internamente.
 */
export function FreeTierLimitBanner({
  entityLabel = 'cobranças',
  withContainer = true,
}: FreeTierLimitBannerProps) {
  const navigate = useNavigate();
  const { hasFinancialModule, isLoadingLimits } = useResourceLimits();
  const { chargeCount, isLoading: isLoadingCount } = useFinancialChargeCount();

  // Não renderiza nada quando o tenant já tem o add-on contratado
  // ou enquanto os limites/contagem ainda estão carregando.
  if (hasFinancialModule || isLoadingLimits || isLoadingCount) return null;

  const isAtLimit = chargeCount >= 10;

  const fullDescription = isAtLimit
    ? `Você atingiu o limite de 10 ${entityLabel}. Contrate o módulo Financeiro para cadastros ilimitados.`
    : `Você possui até 10 ${entityLabel} gratuitas. Contrate o módulo Financeiro para cadastros ilimitados.`;

  const shortDescription = isAtLimit
    ? 'Limite atingido. Contrate o módulo Financeiro.'
    : `${chargeCount}/10 ${entityLabel} usadas.`;

  // Card visual do banner — extraído para reutilização com/sem container externo
  const bannerCard = (
    <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-amber-950/30">
      {/* Gradient decorativo sutil */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-orange-500/10 to-amber-500/5" />

      <div className="relative py-4 px-4 sm:py-5 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        {/* Ícone com gradiente e blur */}
        <div className="relative flex-shrink-0">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 blur-lg" />
          <div className="relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/20">
            <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
        </div>

        {/* Conteúdo textual */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-sm sm:text-base text-amber-700 dark:text-amber-300">
              {isAtLimit ? 'Limite atingido' : 'Módulo gratuito limitado'}
            </h3>
            <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px] sm:text-xs">
              {chargeCount}/10
            </Badge>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            <span className="hidden sm:inline">{fullDescription}</span>
            <span className="sm:hidden">{shortDescription}</span>
          </p>
        </div>

        {/* Botões de ação — empilhados em mobile */}
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none border-amber-500/30 text-amber-600 hover:bg-amber-500/10 hover:border-amber-500/50 transition-all"
            onClick={() => navigate('/tenant/plans')}
          >
            <Package className="mr-1.5 h-4 w-4" />
            Add-on
          </Button>
          <Button
            size="sm"
            className="flex-1 sm:flex-none bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md shadow-amber-500/20 hover:shadow-lg hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
            onClick={() => navigate('/tenant/plans')}
          >
            <Crown className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">Ver Planos</span>
            <span className="sm:hidden">Upgrade</span>
          </Button>
        </div>
      </div>
    </Card>
  );

  if (!withContainer) return bannerCard;

  return <div className="shrink-0 px-4 py-3 border-b">{bannerCard}</div>;
}

export default FreeTierLimitBanner;
